import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Button, Modal } from 'react-native';
import '../components/CommonButton.css'; // Importa stili comuni per i pulsanti
import { loadTeams, loadMatches, saveBracket, saveMatches } from '../utils/storage';
import RotateIcon from '../components/RotateIcon';
import { ChartBarIcon } from '../components/FencingIcons';

// Funzione per creare bracket per competizioni individuali
const createIndividualBracket = (teams) => {
  console.log('Creazione bracket individuale per', teams.length, 'tiratori');

  // Prepara gli atleti qualificati con i loro seed
  const players = teams.map((team, idx) => ({
    ...team,
    seed: idx + 1,
  }));

  // Calcola il numero di round in base al numero di tiratori
  const numRounds = Math.ceil(Math.log2(players.length));
  const totalPositions = Math.pow(2, numRounds);

  // Crea i match vuoti per ogni round
  const rounds = [];
  for (let r = 0; r < numRounds; r++) {
    const roundMatches = [];
    const matchesInRound = Math.pow(2, numRounds - r - 1);

    for (let m = 0; m < matchesInRound; m++) {
      roundMatches.push({
        matchId: `${r}-${m}`,
        team1: r === 0 ? null : { name: 'TBD' },
        team2: r === 0 ? null : { name: 'TBD' },
        winner: null,
        score1: null,
        score2: null,
        completed: false
      });
    }

    rounds.push(roundMatches);
  }

  // Distribuisci i giocatori nel primo round secondo la logica di seed (1 vs ultimo, 2 vs penultimo, ecc)
  // Aggiungi BYE dove necessario
  for (let i = 0; i < totalPositions; i++) {
    const matchIdx = Math.floor(i / 2);
    const isTeam1 = i % 2 === 0;

    const player = players[i] || { name: 'BYE', seed: i + 1, isBye: true };

    if (isTeam1) {
      rounds[0][matchIdx].team1 = player;
    } else {
      rounds[0][matchIdx].team2 = player;
    }
  }

  return { players, matches: rounds };
};

// Funzione per creare bracket per competizioni a squadre
const createTeamEliminationBracket = (teams, qualifiedTeams) => {
  console.log('Creazione bracket a squadre per', teams.length, 'squadre');

  // Per ora usiamo la stessa logica del bracket individuale
  const { matches } = createIndividualBracket(teams, qualifiedTeams);
  return matches;
};

const RoundRobinTable = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Stato per il link al tabellone eliminazione
  const [showBracketLink, setShowBracketLink] = useState(() => {
    return !!localStorage.getItem('bracket');
  });
  const [bracketLinkTimestamp, setBracketLinkTimestamp] = useState(() => {
    return localStorage.getItem('bracket_timestamp') || '';
  });

  // Stato per la modal di conferma
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmCallback, setConfirmCallback] = useState(null);

  // --- MIGRAZIONE DATI MATCHES ---
  // Garantisce che ogni match abbia tutti i campi necessari e coerenti
  function migrateMatches(matches) {
    return matches.map(m => ({
      ...m,
      scoreA: typeof m.scoreA === 'number' ? m.scoreA : 0,
      scoreB: typeof m.scoreB === 'number' ? m.scoreB : 0,
      completed: typeof m.completed === 'boolean' ? m.completed : false,
      teamA: typeof m.teamA !== 'undefined' ? m.teamA : 0,
      teamB: typeof m.teamB !== 'undefined' ? m.teamB : 0,
    }));
  }

  // --- MIGRAZIONE DATI TEAMS ---
  // Garantisce che ogni team abbia id e pool coerenti
  function migrateTeams(teams) {
    return teams.map((t, idx) => {
      // Assicurati che ogni team abbia un nome coerente
      const memberName = Array.isArray(t.members) && t.members.length > 0 ? t.members[0] : `Team ${idx}`;
      // Assicurati che ci sia sempre uno stableId
      const stableId = t.stableId || `team_${idx}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

      return {
        ...t,
        id: typeof t.id !== 'undefined' ? t.id : idx,
        pool: typeof t.pool !== 'undefined' ? t.pool : 0,
        stableId: stableId,
        // Aggiungi un campo per semplificare il debug
        _memberName: memberName
      };
    });
  }

  // Carica sempre i dati aggiornati da localStorage
  const teams = useMemo(() => {
    const localTeams = loadTeams();
    if (localTeams && localTeams.length) return migrateTeams(localTeams);
    if (location.state && location.state.teams) return migrateTeams(location.state.teams);
    return [];
  }, [location.state]);
  const matches = useMemo(() => {
    const localMatches = loadMatches();
    if (localMatches && localMatches.length) return migrateMatches(localMatches);
    if (location.state && location.state.matches) return migrateMatches(location.state.matches);
    return [];
  }, [location.state]);
  const [standings, setStandings] = useState([]);
  const editableMatches = matches;

  // Funzione di reset locale
  const handleResetTournament = () => {
    localStorage.removeItem('teams');
    localStorage.removeItem('matches');
    localStorage.removeItem('bracket');
    window.location.reload();
  };

  const calculateStandings = useCallback(() => {
    console.log('SOLUZIONE RADICALE: Calcolo classifica con nomi tiratori...');

    // Salva i dati originali per debug
    console.log('Teams originali:', teams);
    console.log('Matches originali:', editableMatches);

    // SOLUZIONE RADICALE: Invece di basarci sugli indici o ID stabili,
    // usiamo i nomi dei tiratori come chiave per il match

    // Fase 1: Crea una mappa dei nomi dei tiratori verso le loro statistiche
    const nameToStats = {};

    // Inizializza le statistiche per ogni nome di tiratore/squadra
    teams.forEach(team => {
      // Per squadre: usa team.name
      // Per individual: members[0] può essere stringa o oggetto
      let name = null;

      if (team && team.name) {
        // Modalità squadre: usa il nome del team
        name = team.name;
      } else if (team && team.members && team.members.length > 0) {
        const member = team.members[0];
        if (typeof member === 'string') {
          name = member;
        } else if (member && member.name) {
          name = member.name;
        }
      }

      // Se il nome è valido, inizializza le statistiche
      if (name && typeof name === 'string') {
        nameToStats[name] = {
          name,
          wins: 0,
          losses: 0,
          pointsScored: 0,
          pointsConceded: 0,
          matchesPlayed: 0,
          // Manteniamo un riferimento all'indice originale per il sorting finale
          originalIndex: teams.indexOf(team)
        };
      }
    });

    console.log('Mappa nomi inizializzata:', nameToStats);

    // Fase 2: Elabora tutti i match completati
    editableMatches.forEach(match => {
      if (!match.completed) return;

      // Ottieni i nomi dei tiratori dai team
      const teamA = teams[match.teamA];
      const teamB = teams[match.teamB];

      // Estrai il nome dalle squadre (supporta sia team.name che members[0])
      let nameA = null;
      let nameB = null;

      if (teamA) {
        if (teamA.name) {
          nameA = teamA.name;
        } else if (teamA.members && teamA.members.length > 0) {
          const member = teamA.members[0];
          nameA = typeof member === 'string' ? member : member?.name;
        }
      }

      if (teamB) {
        if (teamB.name) {
          nameB = teamB.name;
        } else if (teamB.members && teamB.members.length > 0) {
          const member = teamB.members[0];
          nameB = typeof member === 'string' ? member : member?.name;
        }
      }

      console.log(`Elaboro match: ${nameA} vs ${nameB} = ${match.scoreA}-${match.scoreB}`);

      // Aggiorna le statistiche usando i NOMI come chiave (non gli indici)
      if (nameToStats[nameA] && nameToStats[nameB]) {
        // Aggiorna stoccate date/subite
        nameToStats[nameA].pointsScored += match.scoreA;
        nameToStats[nameA].pointsConceded += match.scoreB;
        nameToStats[nameB].pointsScored += match.scoreB;
        nameToStats[nameB].pointsConceded += match.scoreA;

        // Determina il vincitore considerando sia punteggio che tempo scaduto
        if (match.scoreA > match.scoreB) {
          nameToStats[nameA].wins += 1;
          nameToStats[nameB].losses += 1;
        } else if (match.scoreB > match.scoreA) {
          nameToStats[nameB].wins += 1;
          nameToStats[nameA].losses += 1;
        } else if (match.byTimeExpired && match.winner) {
          const winnerIsA = match.winner === 1 || match.winner === 'A' || match.winner === 'a';
          const winnerIsB = match.winner === 2 || match.winner === 'B' || match.winner === 'b';

          if (winnerIsA) {
            nameToStats[nameA].wins += 1;
            nameToStats[nameB].losses += 1;
          } else if (winnerIsB) {
            nameToStats[nameB].wins += 1;
            nameToStats[nameA].losses += 1;
          }
        }

        // Aggiorna il conteggio match
        nameToStats[nameA].matchesPlayed += 1;
        nameToStats[nameB].matchesPlayed += 1;
      } else {
        console.warn(`Statistiche mancanti per ${nameA} o ${nameB}`);
      }
    });

    console.log('Statistiche elaborate:', nameToStats);

    // Fase 3: Costruisci l'array delle statistiche
    const statsArray = Object.values(nameToStats);

    // Fase 4: Completa le statistiche e prepara per il sorting
    statsArray.forEach(stats => {
      stats.pointsDiff = stats.pointsScored - stats.pointsConceded;
      stats.VA = stats.matchesPlayed > 0 ? (stats.wins / stats.matchesPlayed) : 0;
    });

    // Fase 5: Ricrea l'array teamStats per compatibilità con il resto del codice
    const teamStats = teams.map((team, index) => {
      // Cerca le statistiche per questo team usando il nome del membro principale
      const memberName = team.members && team.members.length > 0 ? team.members[0] : null;

      // Se abbiamo statistiche per questo nome, usale
      const stats = memberName ? nameToStats[memberName] : null;

      if (stats) {
        return {
          id: index,
          name: team.name || memberName,
          wins: stats.wins,
          losses: stats.losses,
          pointsScored: stats.pointsScored,
          pointsConceded: stats.pointsConceded,
          pointsDiff: stats.pointsDiff,
          matchesPlayed: stats.matchesPlayed,
          VA: stats.VA,
          members: Array.isArray(team.members) ? team.members : [],
          stableId: team.stableId
        };
      } else {
        // Se non abbiamo statistiche, crea un'entry vuota
        return {
          id: index,
          name: team.name || `Team ${index}`,
          wins: 0,
          losses: 0,
          pointsScored: 0,
          pointsConceded: 0,
          pointsDiff: 0,
          matchesPlayed: 0,
          VA: 0,
          members: Array.isArray(team.members) ? team.members : [],
          stableId: team.stableId
        };
      }
    });

    console.log('TeamStats finale:', teamStats);

    // Fase 6: Ordina per classifica
    const sortedStandings = teamStats.slice().sort((a, b) => {
      if (b.VA !== a.VA) return b.VA - a.VA;
      if (b.pointsDiff !== a.pointsDiff) return b.pointsDiff - a.pointsDiff;
      return b.pointsScored - a.pointsScored;
    });
    setStandings(sortedStandings);
  }, [editableMatches, teams]);

  useEffect(() => {
    calculateStandings();
  }, [calculateStandings]);

  // DEBUG: Mostra i dati caricati se vuoti o incoerenti
  if (!teams.length || !matches.length) {
    return (
      <View style={{ padding: 32, alignItems: 'center', justifyContent: 'center' }}>
        <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#1976d2', marginBottom: 20 }}>
          Nessun dato disponibile
        </Text>
        <Text style={{ fontSize: 16, color: '#666', textAlign: 'center', marginBottom: 30 }}>
          Non è stato trovato nessun incontro pianificato. Verifica di aver configurato correttamente la competizione.
        </Text>

        <View style={{ flexDirection: 'row', justifyContent: 'center', marginTop: 20 }}>
          <TouchableOpacity
            style={{
              backgroundColor: '#4caf50',
              paddingVertical: 12,
              paddingHorizontal: 24,
              borderRadius: 8
            }}
            onPress={() => navigate('/')}
          >
            <Text style={{ color: '#fff', fontWeight: 'bold' }}>Crea Nuova Gara</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // --- ESTRAI SINGOLI ATLETI DA TEAM MULTIPLI ---
  // Dati i teams dal backend, estrae i singoli atleti in ogni girone
  function getAthletesByPool(teams) {
    console.log("INPUT TEAMS:", teams);

    const athletesByPool = {};

    teams.forEach(team => {
      if (typeof team.pool === 'undefined') return;

      // Se team.name è un array, contiene più atleti
      if (Array.isArray(team.name)) {
        // Assicurati che il pool esista
        if (!athletesByPool[team.pool]) athletesByPool[team.pool] = [];

        // Crea un atleta separato per ogni nome nell'array
        team.name.forEach((athleteName, idx) => {
          athletesByPool[team.pool].push({
            id: `${team.id}_${idx}`, // ID virtuale per l'atleta
            name: athleteName,
            originalTeam: team.id,
            pool: team.pool,
            index: idx, // Per identificare l'atleta all'interno del team
            wins: 0,
            losses: 0,
            pointsScored: 0,
            pointsConceded: 0
          });
        });
      }
      // Se è un singolo atleta
      else {
        if (!athletesByPool[team.pool]) athletesByPool[team.pool] = [];
        athletesByPool[team.pool].push({
          id: team.id,
          name: team.name,
          originalTeam: team.id,
          pool: team.pool,
          index: 0, // Singolo atleta
          wins: 0,
          losses: 0,
          pointsScored: 0,
          pointsConceded: 0
        });
      }
    });

    console.log("ATHLETES BY POOL:", JSON.stringify(athletesByPool));
    return athletesByPool;
  }

  // Trova il punteggio tra due atleti specifici
  function findMatchScore(athlete1, athlete2, matches) {
    if (!matches || !matches.length) return null;

    // Supporta sia modalità individual (teamA/teamB come indici) che mixed (squadraA/squadraB come stringhe)
    let matchIndex = -1;
    const match = matches.find((m, idx) => {
      // Check by team indices (individual mode)
      const matchByIndex = (m.teamA === athlete1.originalTeam && m.teamB === athlete2.originalTeam) ||
        (m.teamA === athlete2.originalTeam && m.teamB === athlete1.originalTeam);

      // Check by team names (mixed mode) - confronta con il nome dell'atleta o della squadra
      // Check by team names (mixed mode) - confronta con il nome dell'atleta o della squadra
      // FIX: Assicurarsi che le proprietà esistano prima di confrontare per evitare undefined === undefined
      const matchByName = (m.squadraA && m.squadraB && (
        (m.squadraA === athlete1.name && m.squadraB === athlete2.name) ||
        (m.squadraA === athlete2.name && m.squadraB === athlete1.name)
      )) ||
        (m.squadraA && m.squadraB && athlete1.team && athlete2.team && (
          (m.squadraA === athlete1.team && m.squadraB === athlete2.team) ||
          (m.squadraA === athlete2.team && m.squadraB === athlete1.team)
        ));

      if (matchByIndex || matchByName) {
        return true;
      }
      return false;
    });

    if (!match) return null;

    matchIndex = editableMatches.indexOf(match);

    // Determina se athlete1 è "teamA" o "squadraA"
    // FIX: Aggiunto check per evitare che undefined === undefined dia true
    const athlete1IsA = (match.teamA === athlete1.originalTeam) ||
      (match.squadraA && match.squadraA === athlete1.name) ||
      (match.squadraA && athlete1.team && match.squadraA === athlete1.team);

    console.log(`[findMatchScore] ${athlete1.name} vs ${athlete2.name}: match.teamA=${match.teamA}, athlete1.originalTeam=${athlete1.originalTeam}, athlete1IsA=${athlete1IsA}, scoreA=${match.scoreA}, scoreB=${match.scoreB}`);

    // Determina quale è il punteggio dell'atleta 1 e dell'atleta 2
    const score1 = athlete1IsA ? (match.scoreA ?? match.punteggioA ?? 0) : (match.scoreB ?? match.punteggioB ?? 0);
    const score2 = athlete1IsA ? (match.scoreB ?? match.punteggioB ?? 0) : (match.scoreA ?? match.punteggioA ?? 0);

    // Determina chi ha vinto, considerando anche il tempo scaduto
    let winner = null;
    if (score1 > score2) {
      winner = 1; // Atleta 1 ha vinto
    } else if (score2 > score1) {
      winner = 2; // Atleta 2 ha vinto
    } else if (match.byTimeExpired && match.winner) {
      // Parità con tempo scaduto e vincitore designato
      const matchWinner = match.winner;
      const isTeam1Winner = matchWinner === 1 || matchWinner === 'A' || matchWinner === 'a';
      const isTeam2Winner = matchWinner === 2 || matchWinner === 'B' || matchWinner === 'b';

      // Converti la vittoria del team in vittoria dell'atleta
      if (athlete1IsA) {
        winner = isTeam1Winner ? 1 : (isTeam2Winner ? 2 : null);
      } else {
        winner = isTeam2Winner ? 1 : (isTeam1Winner ? 2 : null);
      }
    }

    return {
      id: match.id,
      matchIndex: matchIndex,
      score1: score1,
      score2: score2,
      completed: match.completed || match.stato === 'completato',
      winner: winner  // Indica chi ha vinto: 1=atleta1, 2=atleta2, null=pareggio
    };
  }

  // Funzione per calcolare le statistiche di ogni atleta nel girone
  function calculateAthleteStats(athletes, matches) {
    console.log("CALCULATE STATS - INPUT ATHLETES:", athletes.length, athletes.map(a => a.name));
    console.log("MATCH DATA for stats:", matches ? matches.length : 0, "matches available");

    const statsMap = {}; // Mappa per statistiche aggiornate

    // Inizializza statistiche - Utilizziamo nome+pool come chiave unica per evitare collisioni
    athletes.forEach((athlete) => {
      const uniqueKey = `${athlete.name}_${athlete.pool}`; // Chiave unica

      statsMap[uniqueKey] = {
        ...athlete,
        uniqueKey, // Aggiungiamo la chiave unica
        wins: 0,
        losses: 0,
        pointsScored: 0,
        pointsConceded: 0,
        matchesPlayed: 0,
        pointsDiff: 0
      };
    });

    // Calcola statistiche SOLO se ci sono match completati (supporta flag completed o stato stringa)
    const completedMatches = matches ? matches.filter(m => m && (m.completed || m.stato === 'completato')) : [];
    console.log("COMPLETED MATCHES:", completedMatches.length);

    if (completedMatches.length > 0) {
      completedMatches.forEach(match => {
        // Trova i due atleti coinvolti - supporta sia individual che mixed mode
        // Per individual mode: originalTeam corrisponde a teamA/teamB
        // Per mixed mode: confronta con squadraA/squadraB
        let athleteA = null;
        let athleteB = null;

        // Verifica rigorosa: usa gli indici SOLO se sono validi E diversi
        let useIndices = typeof match.teamA === 'number' && typeof match.teamB === 'number';
        if (useIndices && match.teamA === match.teamB) {
          useIndices = false; // Corrupt data (self-match by index), force name lookup
        }

        if (useIndices) {
          if (typeof match.teamA === 'number') athleteA = athletes.find(a => a.originalTeam === match.teamA);
          if (typeof match.teamB === 'number') athleteB = athletes.find(a => a.originalTeam === match.teamB);
        }

        // Fallback per mixed mode: cerca per nome squadra (Priorità al nome esatto, robusto)
        if (!athleteA && match.squadraA) {
          const nameToFind = String(match.squadraA).trim();
          athleteA = athletes.find(a => String(a.name).trim() === nameToFind);
          if (!athleteA) athleteA = athletes.find(a => String(a.team || '').trim() === nameToFind);
        }
        if (!athleteB && match.squadraB) {
          const nameToFind = String(match.squadraB).trim();
          athleteB = athletes.find(a => String(a.name).trim() === nameToFind);
          if (!athleteB) athleteB = athletes.find(a => String(a.team || '').trim() === nameToFind);
        }

        if (!athleteA || !athleteB) {
          console.log('[calculateAthleteStats] Atleti non trovati per match:', match);
          return;
        }

        const keyA = `${athleteA.name}_${athleteA.pool}`;
        const keyB = `${athleteB.name}_${athleteB.pool}`;

        if (keyA === keyB) {
          console.warn('[calculateAthleteStats] Identificato stesso atleta per entrambi i lati match! Skipping.', keyA, match);
          return;
        }

        // Aggiorna i match giocati
        statsMap[keyA].matchesPlayed += 1;
        statsMap[keyB].matchesPlayed += 1;

        // Usa scoreA/scoreB o punteggioA/punteggioB (per compatibilità mixed)
        const scoreA = match.scoreA ?? match.punteggioA ?? 0;
        const scoreB = match.scoreB ?? match.punteggioB ?? 0;

        // Aggiorna punteggi
        statsMap[keyA].pointsScored += scoreA;
        statsMap[keyA].pointsConceded += scoreB;
        statsMap[keyB].pointsScored += scoreB;
        statsMap[keyB].pointsConceded += scoreA;

        // Determina il vincitore, considerando anche il tempo scaduto
        let winnerTeam = null;

        // Log dei dati del match per debug
        console.log(`Match ${match.id || 'unknown'} dati:`, {
          scoreA: match.scoreA,
          scoreB: match.scoreB,
          teamA: match.teamA,
          teamB: match.teamB,
          byTimeExpired: match.byTimeExpired,
          winner: match.winner,
          completed: match.completed
        });

        if (scoreA > scoreB) {
          // A ha più punti, quindi vince
          winnerTeam = 'A';
          console.log(`Match ${match.id || 'unknown'}: Vittoria squadra A per punteggio superiore`);
        } else if (scoreB > scoreA) {
          // B ha più punti, quindi vince
          winnerTeam = 'B';
          console.log(`Match ${match.id || 'unknown'}: Vittoria squadra B per punteggio superiore`);
        } else if (match.byTimeExpired && match.winner) {
          // Se abbiamo un vincitore designato per tempo scaduto
          const matchWinner = match.winner;
          console.log(`Match ${match.id || 'unknown'} con parità e tempo scaduto, winner:`, matchWinner);

          if (matchWinner === 1 || matchWinner === 'A' || matchWinner === 'a') {
            winnerTeam = 'A';
            console.log(`- Identificato come vittoria squadra A`);
          } else if (matchWinner === 2 || matchWinner === 'B' || matchWinner === 'b') {
            winnerTeam = 'B';
            console.log(`- Identificato come vittoria squadra B`);
          }
        }

        // Aggiorna vincite/perdite in base al vincitore
        if (winnerTeam === 'A') {
          statsMap[keyA].wins += 1;
          statsMap[keyB].losses += 1;
          console.log(`Aggiunta vittoria a ${athleteA.name}, ora ha ${statsMap[keyA].wins} vittorie`);
        } else if (winnerTeam === 'B') {
          statsMap[keyB].wins += 1;
          statsMap[keyA].losses += 1;
          console.log(`Aggiunta vittoria a ${athleteB.name}, ora ha ${statsMap[keyB].wins} vittorie`);
        } else {
          console.log(`Match ${match.id || 'unknown'}: Nessun vincitore designato`);
        }
        // In caso di parità senza vincitore designato, non assegniamo punti
      });
    }

    // Calcola anche la differenza stoccate e il rapporto V/A
    Object.values(statsMap).forEach(stat => {
      stat.pointsDiff = stat.pointsScored - stat.pointsConceded;
      stat.VA = stat.matchesPlayed > 0 ? (stat.wins / stat.matchesPlayed) : 0;
    });

    // Converti mappa in array 
    const result = Object.values(statsMap);
    console.log("STATS RESULT BEFORE SORT:", result.length, result.map(a => a.name));

    // Ordina risultati (anche se tutti gli atleti hanno gli stessi valori)
    const sorted = [...result].sort((a, b) => {
      if (b.VA !== a.VA) return b.VA - a.VA;
      if (b.pointsDiff !== a.pointsDiff) return b.pointsDiff - a.pointsDiff;
      return b.pointsScored - a.pointsScored;
    });

    console.log("STATS RESULT AFTER SORT:", sorted.length, sorted.map(a => a.name));
    return sorted;
  }

  // Funzione per combinare tutti gli atleti di tutti i gironi in una classifica generale
  function generateGeneralRanking(athletesByPool) {
    console.log("GENERATING GENERAL RANKING - INPUT:", JSON.stringify(athletesByPool));

    // Metodo più esplicito per unire tutti gli atleti
    let allAthletes = [];

    // Itera su tutti i gironi e accumula gli atleti
    Object.keys(athletesByPool).forEach(poolId => {
      console.log(`POOL ${poolId} - ATHLETES:`, athletesByPool[poolId].length);
      // Assicuriamoci che ogni atleta abbia un ID univoco per pool
      const athletesWithUniqueId = athletesByPool[poolId].map((athlete, idx) => ({
        ...athlete,
        id: `${poolId}_${idx}` // Garantisce ID univoco per girone e indice
      }));
      allAthletes = [...allAthletes, ...athletesWithUniqueId];
    });

    console.log("ALL ATHLETES COMBINED:", allAthletes.length);
    console.log("ATHLETES DETAILS:", allAthletes.map(a => `${a.name} (G${a.pool + 1})`));

    // Calcola le statistiche per tutti gli atleti
    const ranking = calculateAthleteStats(allAthletes, editableMatches);
    console.log("FINAL RANKING:", ranking.length, ranking.map(a => `${a.name} (G${a.pool + 1})`));

    return ranking;
  }

  // Rendering tabelle round robin per gironi (RIVISTO)
  let poolTables = null;

  // Estrai i singoli atleti dai team per girone
  const athletesByPool = getAthletesByPool(teams);
  const poolKeys = Object.keys(athletesByPool);

  // Genera la classifica generale con tutti gli atleti di tutti i gironi
  const generalRanking = generateGeneralRanking(athletesByPool);

  console.log("POOL KEYS:", poolKeys);

  // RENDER SEMPRE LA TABELLA PER OGNI GIRONE, ANCHE SE CE N'È SOLO UNO
  poolTables = poolKeys.map(poolId => {
    const poolAthletes = athletesByPool[poolId];

    console.log(`POOL ${poolId} ATHLETES:`, JSON.stringify(poolAthletes));

    // Filtra i match pertinenti a questo girone
    const poolMatches = editableMatches.filter(m =>
      teams.some(t => t.id === m.teamA && t.pool.toString() === poolId) &&
      teams.some(t => t.id === m.teamB && t.pool.toString() === poolId)
    );

    // Calcola statistiche per gli atleti di questo girone
    const athleteStats = calculateAthleteStats(poolAthletes, poolMatches);

    return (
      <View key={poolId} style={{ marginBottom: 32, backgroundColor: '#f5fafd', borderRadius: 12, padding: 12, border: '1.5px solid #1976d2' }}>
        <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#1976d2', marginBottom: 6 }}>Girone {parseInt(poolId) + 1}</Text>

        {/* Tabella round robin del girone: una riga per atleta */}
        <ScrollView horizontal style={{ marginBottom: 10 }}>
          <View>
            {/* Intestazione: numeri atleti */}
            <View style={styles.tableHeader}>
              <Text style={[styles.headerCell, { minWidth: 40 }]}>#</Text>
              <Text style={[styles.headerCell, { minWidth: 120 }]}>Nome</Text>
              {poolAthletes.map((_, idx) => (
                <Text key={idx} style={[styles.headerCell, { minWidth: 70, backgroundColor: '#e3f2fd', color: '#1976d2' }]}>
                  {idx + 1}
                </Text>
              ))}
            </View>

            {/* Una riga per ogni atleta */}
            {poolAthletes.map((athlete, rowIdx) => (
              <View key={rowIdx} style={styles.tableRow}>
                <Text style={[styles.cell, { minWidth: 40, textAlign: 'center' }]}>{rowIdx + 1}</Text>
                <Text style={[styles.cell, { minWidth: 120, textAlign: 'center' }]}>{athlete.name}</Text>

                {poolAthletes.map((opponent, colIdx) => {
                  if (rowIdx === colIdx) {
                    return <Text key={colIdx} style={[styles.cell, { minWidth: 70, backgroundColor: '#b0b0b0', color: '#b0b0b0' }]}> </Text>;
                  }

                  // Cerca il match tra l'atleta sulla riga e l'atleta sulla colonna
                  const matchResult = findMatchScore(athlete, opponent, poolMatches);

                  // Determina il display del punteggio con la "V" per il vincitore
                  let displayScore = '-';
                  if (matchResult && typeof matchResult.score1 === 'number' && matchResult.completed) {
                    // Se l'atleta sulla riga (atleta 1) ha vinto, aggiungi "V" davanti al punteggio
                    if (matchResult.winner === 1) {
                      displayScore = `V${matchResult.score1}`;
                    } else {
                      displayScore = `${matchResult.score1}`;
                    }
                  }

                  // Evidenzia V se presente con un colore diverso
                  const hasVictory = displayScore.startsWith('V');

                  return (
                    <TouchableOpacity
                      key={colIdx}
                      onPress={() => {
                        // Carica i match esistenti
                        const matches = loadMatches() || [];

                        if (!matchResult) {
                          // Se non esiste ancora un match, ne creiamo uno
                          console.log(`Creating new match between ${athlete.name} and ${opponent.name}`);

                          // Creazione di un nuovo match
                          const newMatch = {
                            id: matches.length, // ID numerico sequenziale
                            teamA: (athlete.id !== undefined ? athlete.id : athlete.originalTeam),
                            teamB: (opponent.id !== undefined ? opponent.id : opponent.originalTeam),
                            squadraA: athlete.name,
                            squadraB: opponent.name,
                            scoreA: 0,
                            scoreB: 0,
                            completed: false,
                            winner: null,
                            pool: parseInt(poolId)
                          };

                          // Aggiungi il nuovo match all'array
                          matches.push(newMatch);
                          saveMatches(matches);

                          // Naviga a ScoreKeeper
                          navigate('/score', {
                            state: {
                              roundIndex: parseInt(poolId),
                              matchId: newMatch.id,
                              matchIndex: matches.length - 1,
                              teamA: athlete,
                              teamB: opponent
                            }
                          });
                        } else {
                          // Se esiste già un match, usa l'indice trovato da findMatchScore
                          console.log(`Navigating to ScoreKeeper for existing match id=${matchResult.id}, index=${matchResult.matchIndex}`);

                          navigate('/score', {
                            state: {
                              roundIndex: parseInt(poolId),
                              matchId: matchResult.matchIndex >= 0 ? matchResult.matchIndex : matchResult.id,
                              matchIndex: matchResult.matchIndex,
                              teamA: athlete,
                              teamB: opponent
                            }
                          });
                        }
                      }}
                      style={{
                        minWidth: 70,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: hasVictory ? '#e8f5e9' : (matchResult && matchResult.completed ? '#e3f2fd' : 'transparent'),
                        borderRadius: 8,
                        overflow: 'hidden',
                        flex: 1,
                        padding: 4
                      }}
                    >
                      <Text style={{
                        textAlign: 'center',
                        color: hasVictory ? '#2e7d32' : (matchResult && matchResult.completed ? '#1976d2' : '#888'),
                        fontWeight: matchResult && matchResult.completed ? 'bold' : 'normal',
                        fontSize: matchResult && matchResult.completed ? 18 : 15,
                      }}>
                        {displayScore}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            ))}
          </View>
        </ScrollView>

        {/* Classifica girone */}
        <View style={{ marginTop: 6, marginBottom: 6 }}>
          <Text style={{ fontWeight: 'bold', color: '#1976d2', marginBottom: 4 }}>Classifica Girone</Text>
          <ScrollView horizontal style={{ marginBottom: 10 }}>
            <View>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4, paddingBottom: 4, borderBottomWidth: 1, borderBottomColor: '#ddd' }}>
                <Text style={{ minWidth: 30, textAlign: 'center', fontWeight: 'bold' }}>Pos</Text>
                <Text style={{ minWidth: 120, fontWeight: 'bold' }}>Atleta</Text>
                <Text style={{ minWidth: 40, textAlign: 'center', fontWeight: 'bold' }}>V/M</Text>
                <Text style={{ minWidth: 40, textAlign: 'center', fontWeight: 'bold' }}>V</Text>
                <Text style={{ minWidth: 40, textAlign: 'center', fontWeight: 'bold' }}>F</Text>
                <Text style={{ minWidth: 40, textAlign: 'center', fontWeight: 'bold' }}>S</Text>
                <Text style={{ minWidth: 40, textAlign: 'center', fontWeight: 'bold' }}>+/-</Text>
              </View>

              {athleteStats.map((athlete, idx) => (
                <View key={idx} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 2 }}>
                  <Text style={{ minWidth: 30, textAlign: 'center' }}>{idx + 1}</Text>
                  <Text style={{ minWidth: 120 }}>{athlete.name}</Text>
                  <Text style={{ minWidth: 40, textAlign: 'center' }}>{athlete.matchesPlayed > 0 ? (athlete.wins / athlete.matchesPlayed).toFixed(2) : '0.00'}</Text>
                  <Text style={{ minWidth: 40, textAlign: 'center' }}>{athlete.wins}</Text>
                  <Text style={{ minWidth: 40, textAlign: 'center' }}>{athlete.pointsScored}</Text>
                  <Text style={{ minWidth: 40, textAlign: 'center' }}>{athlete.pointsConceded}</Text>
                  <Text style={{ minWidth: 40, textAlign: 'center' }}>{athlete.pointsDiff}</Text>
                </View>
              ))}
            </View>
          </ScrollView>
        </View>
      </View>
    );
  });

  // Filtra match validi (più permissivo)
  const validEditableMatches = editableMatches.filter(match => {
    const teamA = teams[match.teamA];
    const teamB = teams[match.teamB];
    if (!teamA || !teamB) return false;
    if (!Array.isArray(teamA.members) || !Array.isArray(teamB.members)) return false;
    if (teamA.members.length === 0 || teamB.members.length === 0) return false;
    // Se memberA/memberB sono definiti, devono essere indici validi
    if (typeof match.memberA !== 'undefined' && (typeof match.memberA !== 'number' || !teamA.members[match.memberA])) return false;
    if (typeof match.memberB !== 'undefined' && (typeof match.memberB !== 'number' || !teamB.members[match.memberB])) return false;
    return true;
  });

  // Se tutti i match sono corrotti, mostra avviso
  if (editableMatches.length > 0 && validEditableMatches.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={{ color: 'red', marginTop: 40, fontWeight: 'bold' }}>Tutti i dati degli incontri risultano corrotti o incompleti.</Text>
        <Text style={{ marginTop: 10 }}>Premi "Resetta Torneo" per cancellare i dati e ripartire.</Text>
        <Button title="Resetta Torneo" onPress={handleResetTournament} color="#d32f2f" />
      </View>
    );
  }



  const sbarramento = parseInt(localStorage.getItem('individual_sbarramento') || '0', 10);





  async function handleGeneraEliminazione() {
    // Verifica se esiste già un tabellone di eliminazione
    const existingBracket = JSON.parse(localStorage.getItem('bracket'));

    if (existingBracket) {
      // Se esiste già un tabellone, mostra la modal di conferma
      showConfirmDialog(
        'Attenzione',
        'Esiste già un tabellone di eliminazione. Generando un nuovo tabellone, i dati precedenti andranno persi.',
        () => generateBracketFlow() // Callback per la conferma
      );
      return;
    }

    // Se non esiste un bracket precedente, procedi direttamente
    generateBracketFlow();
  }

  // Funzione per mostrare il dialog di conferma
  const showConfirmDialog = (title, message, onConfirm) => {
    setConfirmCallback(() => onConfirm); // Salviamo la callback
    setShowConfirmModal(true);
  };

  // Funzione che contiene il flusso di generazione del tabellone
  const generateBracketFlow = () => {

    // Salva sbarramento corrente nei log
    console.log(`Generazione bracket con sbarramento=${sbarramento}`);

    // Flag per tracciare che il bracket è stato generato via pulsante
    localStorage.setItem('bracket_source', 'button_generated');

    // Salviamo lo sbarramento per sicurezza
    localStorage.setItem('individual_sbarramento', sbarramento.toString());

    // Verifica se la competizione è individuale o a squadre
    const competitionType = localStorage.getItem('competitionType') || 'individual';

    // Determina quanti atleti qualificati in base allo sbarramento
    const qualifiedAthletes = standings.slice(0, sbarramento);
    localStorage.setItem('qualified_athletes', JSON.stringify(qualifiedAthletes));
    console.log(`Atleti qualificati all'eliminazione diretta: ${qualifiedAthletes.length}`);

    // Mappa i team in base agli atleti qualificati
    const teams = [];
    qualifiedAthletes.forEach((athlete, index) => {
      const teamObj = {
        id: index,
        name: athlete.name,
        score: 0,
        members: [{
          id: index,
          name: athlete.name
        }]
      };
      teams.push(teamObj);
    });

    // Genera il bracket di eliminazione diretta
    let bracket;

    if (competitionType === 'individual') {
      const { matches } = createIndividualBracket(teams, qualifiedAthletes);
      bracket = matches;

      // Avanzamento bye prima di salvare
      const advanceByeInBracket = (inputBracket) => {
        if (!Array.isArray(inputBracket) || !inputBracket.length) {
          return inputBracket;
        }

        const updated = inputBracket.map(round => round.map(m => ({ ...m })));
        for (let r = 0; r < updated.length - 1; r++) {
          for (let m = 0; m < updated[r].length; m++) {
            const match = updated[r][m];
            // Se uno dei due team è BYE e l'altro è reale, avanza la squadra reale
            if ((match.team1?.name === 'BYE' && match.team2?.name !== 'BYE' && match.team2?.name !== 'TBD') ||
              (match.team2?.name === 'BYE' && match.team1?.name !== 'BYE' && match.team1?.name !== 'TBD')) {
              const winner = match.team1?.name === 'BYE' ? match.team2 : match.team1;
              match.winner = match.team1?.name === 'BYE' ? 2 : 1;
              match.score1 = match.team1?.name === 'BYE' ? 0 : 1;
              match.score2 = match.team2?.name === 'BYE' ? 0 : 1;
              // Trova posizione nel round successivo
              const nextRound = updated[r + 1];
              const nextMatchIdx = Math.floor(m / 2);
              if (nextRound && nextRound[nextMatchIdx]) {
                if (m % 2 === 0) nextRound[nextMatchIdx].team1 = winner;
                else nextRound[nextMatchIdx].team2 = winner;
              }
            }
          }
        }
        return updated;
      };

      // Avanza i bye direttamente qui prima di salvare
      bracket = advanceByeInBracket(bracket);
      saveBracket(bracket);
    } else {
      bracket = createTeamEliminationBracket(teams, qualifiedAthletes);
      saveBracket(bracket);
    }

    // Salva il timestamp per tracciare quando è stato generato il bracket
    const timestamp = Date.now();
    localStorage.setItem('bracket_timestamp', timestamp.toString());

    // Aggiorna lo stato locale
    setBracketLinkTimestamp(timestamp.toString());
    setShowBracketLink(true);

    console.log("Bracket generato e bye avanzati automaticamente");
    console.log("Bracket salvato in localStorage e pronto per la navigazione");

    // Setta un flag per indicare che i dati sono pronti per l'avanzamento bye
    localStorage.setItem('byeAdvancementNeeded', 'true');

    // Aggiungiamo un messaggio per l'utente
    const loadingMessage = document.createElement('div');
    loadingMessage.style.position = 'fixed';
    loadingMessage.style.top = '50%';
    loadingMessage.style.left = '50%';
    loadingMessage.style.transform = 'translate(-50%, -50%)';
    loadingMessage.style.background = 'rgba(0,0,0,0.8)';
    loadingMessage.style.color = 'white';
    loadingMessage.style.padding = '20px';
    loadingMessage.style.borderRadius = '10px';
    loadingMessage.style.zIndex = '9999';
    loadingMessage.innerHTML = 'Preparazione tabellone in corso...';
    document.body.appendChild(loadingMessage);

    // Ritardiamo la navigazione per dare tempo ai dati di essere salvati correttamente
    setTimeout(() => {
      // Forza un reload dopo la navigazione per garantire il caricamento corretto
      localStorage.setItem('forceReloadAfterNavigation', 'true');

      // Naviga al bracket
      navigate('/bracket', {
        state: {
          bracket: bracket,
          teams: teams,
          standings: qualifiedAthletes
        }
      });

      // Rimuovi il messaggio di caricamento
      document.body.removeChild(loadingMessage);
    }, 1000); // Ritardo di 1 secondo
  }

  // --- ANTEPRIMA GIRONI MANUALI ---
  let manualPools = [];
  try {
    manualPools = JSON.parse(localStorage.getItem('individual_manual_pools'));
  } catch { }
  // Se non ci sono manualPools validi, raggruppa per team.pool
  if ((!manualPools || !Array.isArray(manualPools) || manualPools.length < 2) && teams && teams.length > 0 && teams.some(t => typeof t.pool !== 'undefined')) {
    const maxPool = Math.max(...teams.map(t => t.pool || 0));
    manualPools = [];
    for (let i = 0; i <= maxPool; i++) manualPools.push([]);
    teams.forEach(t => {
      if (typeof t.pool !== 'undefined') manualPools[t.pool].push(t.name);
    });
  }

  // saveConfigBeforeNavigation rimossa (inutilizzata)



  // Stili per il titolo e lo spaziatore, simili a quelli in EliminationBracket
  const titleStyle = {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#1976d2',
    textAlign: 'center',
    marginBottom: 2,
    textShadow: '0px 2px 4px rgba(0,0,0,0.06)',
  };

  const underlineStyle = {
    height: 4,
    backgroundColor: '#1976d2',
    width: 72,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 18,
    opacity: 0.2,
  };

  // Modal di conferma personalizzata
  const renderConfirmModal = () => {
    return (
      <Modal
        animationType="fade"
        transparent={true}
        visible={showConfirmModal}
        onRequestClose={() => setShowConfirmModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Attenzione</Text>
            <Text style={styles.modalMessage}>Esiste già un tabellone di eliminazione. Generando un nuovo tabellone, i dati precedenti andranno persi.</Text>

            <View style={styles.modalButtonsContainer}>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalCancelButton]}
                onPress={() => setShowConfirmModal(false)}
              >
                <Text style={styles.modalButtonText}>Annulla</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.modalButton, styles.modalConfirmButton]}
                onPress={() => {
                  setShowConfirmModal(false);
                  if (confirmCallback) {
                    confirmCallback();
                  }
                }}
              >
                <Text style={[styles.modalButtonText, { color: 'white' }]}>Procedi</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  return (
    <ScrollView style={styles.container}>
      {/* Titolo centrato rispetto al container */}
      <View style={{ alignItems: 'center', marginTop: 40, marginBottom: 2 }}>
        {/* Icona suggerimento rotazione (visibile solo su mobile) */}
        <RotateIcon />
        <ChartBarIcon style={{ fontSize: 44, color: '#1976d2', marginBottom: 5 }} />
        <Text style={titleStyle}>Girone</Text>
        <View style={underlineStyle} />
      </View>
      {/* Rendering tabelle round robin per gironi */}
      {poolTables}

      {/* Tabella e classifica generale */}
      <View style={{ marginTop: 12, marginBottom: 24, backgroundColor: '#fffbe7', borderRadius: 12, padding: 12, border: '1.5px solid #ff9800' }}>
        <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#ff9800', marginBottom: 6 }}>Classifica Generale</Text>
        <ScrollView horizontal style={{ marginBottom: 10 }}>
          <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4, paddingBottom: 4, borderBottomWidth: 1, borderBottomColor: '#ddd' }}>
              <Text style={{ minWidth: 30, textAlign: 'center', fontWeight: 'bold' }}>Pos</Text>
              <Text style={{ minWidth: 120, fontWeight: 'bold' }}>Atleta</Text>
              <Text style={{ minWidth: 30, textAlign: 'center', fontWeight: 'bold' }}>G</Text>
              <Text style={{ minWidth: 30, textAlign: 'center', fontWeight: 'bold' }}>V/M</Text>
              <Text style={{ minWidth: 30, textAlign: 'center', fontWeight: 'bold' }}>V</Text>
              <Text style={{ minWidth: 40, textAlign: 'center', fontWeight: 'bold' }}>F</Text>
              <Text style={{ minWidth: 40, textAlign: 'center', fontWeight: 'bold' }}>S</Text>
              <Text style={{ minWidth: 40, textAlign: 'center', fontWeight: 'bold' }}>+/-</Text>
            </View>

            {generalRanking.map((athlete, idx) => (
              <View key={idx} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 2, backgroundColor: idx % 2 === 0 ? '#fff9e6' : 'transparent', padding: 4, borderRadius: 4 }}>
                <Text style={{ minWidth: 30, textAlign: 'center' }}>{idx + 1}</Text>
                <Text style={{ minWidth: 120 }}>{athlete.name}</Text>
                <Text style={{ minWidth: 30, textAlign: 'center' }}>{athlete.pool + 1}</Text>
                <Text style={{ minWidth: 30, textAlign: 'center' }}>{athlete.matchesPlayed > 0 ? (athlete.wins / athlete.matchesPlayed).toFixed(2) : '0.00'}</Text>
                <Text style={{ minWidth: 30, textAlign: 'center' }}>{athlete.wins}</Text>
                <Text style={{ minWidth: 40, textAlign: 'center' }}>{athlete.pointsScored}</Text>
                <Text style={{ minWidth: 40, textAlign: 'center' }}>{athlete.pointsConceded}</Text>
                <Text style={{ minWidth: 40, textAlign: 'center' }}>{athlete.pointsDiff}</Text>
              </View>
            ))}
          </View>
        </ScrollView>
      </View>

      {/* Rimuovo il suggerimento di ruotare lo schermo come richiesto */}

      {/* Contenitore senza padding per larghezza massima */}
      <View style={{ width: '100%' }}>
        {editableMatches.length > 0 && editableMatches.every(m => m.completed) && (
          showBracketLink ? (
            <div style={{ marginVertical: 16 }}>
              {/* Link React Router che funziona come la navbar, causando un remount completo */}
              <Link
                to="/bracket"
                state={{
                  timestamp: bracketLinkTimestamp,
                  forceRefresh: true
                }}
                className="common-button"
              >
                Vai al tabellone eliminazione
              </Link>
              <p style={{ marginTop: 8, fontSize: 14, color: '#555' }}>
                Il tabellone è stato generato. Clicca sul link per visualizzarlo.
              </p>
            </div>
          ) : (
            <TouchableOpacity
              className="common-button"
              style={{
                width: '100%',
                paddingVertical: 12,
                paddingHorizontal: 24,
                borderRadius: 24,
                backgroundColor: '#1976d2',
                marginVertical: 16,
                boxShadow: '0px 3px 4px rgba(25, 118, 210, 0.18)',
                alignItems: 'center'
              }}
              onPress={handleGeneraEliminazione}
            >
              <Text style={{ color: 'white', fontWeight: 'bold', fontSize: 17 }}>Genera tabellone eliminazione</Text>
            </TouchableOpacity>
          )
        )}
      </View>
      {/* Renderizza la modal di conferma */}
      {renderConfirmModal()}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 24,
    paddingRight: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#2196f3',
    padding: 12,
    borderRadius: 12,
    marginBottom: 4,
  },
  headerCell: {
    color: 'white',
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'center',
    borderRadius: 8,
    overflow: 'hidden',
  },
  tableRow: {
    flexDirection: 'row',
    padding: 12,
    borderRadius: 10,
    marginBottom: 2,
    backgroundColor: '#fff',
  },
  evenRow: {
    backgroundColor: '#f5f5f5',
    borderRadius: 10,
  },
  oddRow: {
    backgroundColor: '#e0e0e0',
    borderRadius: 10,
  },
  cell: {
    flex: 1,
    textAlign: 'center',
    borderRadius: 8,
    overflow: 'hidden',
  },
  sectionTitleContainer: {
    marginTop: 24,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  bracketButton: {
    backgroundColor: '#ff9800',
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginVertical: 20,
  },
  bracketButtonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 16,
  },
  backButton: {
    backgroundColor: '#1976d2',
    padding: 10,
    borderRadius: 5,
    marginBottom: 16,
    alignSelf: 'flex-start',
  },
  backButtonText: {
    color: 'white',
    fontWeight: 'bold',
  },
  // Stili per la modal di conferma
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 16,
    color: '#1976d2',
  },
  modalMessage: {
    fontSize: 16,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  modalButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 8,
  },
  modalCancelButton: {
    backgroundColor: '#f5f5f5',
    borderColor: '#e0e0e0',
    borderWidth: 1,
  },
  modalConfirmButton: {
    backgroundColor: '#1976d2',
  },
  modalButtonText: {
    fontWeight: 'bold',
    fontSize: 16,
  },
});

export default RoundRobinTable;